import { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { isValidWalletAddressForNetwork } from '@/pages/Buy/utils'
import { Button } from '@/components/Button/Button'
import { Checkbox } from '@/components/Checkbox'
import { LoadingIcon } from '@/components/LoadingIcon'
import {
  BackButton,
  EllipsisAnimation,
  TextInput,
  ReferralAccountText,
} from '@/components/widget-components'
import { usePageStateStore } from '@/reducers'
import {
  TransactionType,
  TransactionValue,
  useBuySellStore,
} from '@/reducers/transactionReducer'
import { useFeatureFlags } from '@/contexts/FeatureFlagsContext'

import { submitBuy, submitSell } from '@/requests/transaction'
import { getReferralQuote } from '@/requests/quote'
import { Networks, NetworkOptions, NetworkValue } from '@/utils/currencies'
import { SummaryRow } from './SummaryRows'
import { UseEthInfo } from './UseEthInfo'
import { SimpleWalletConnect } from '@/components/Button/AddWalletConnect'
import { SelectedWalletAccountContext } from '@/contexts/WalletAccountProvider'
import * as Sentry from '@sentry/react'
import { saveUserTokenPreference } from '@/utils/userPreferences'
import { useCurrentUser } from '@/hooks/useCurrentUser'
import { useMiniPayProvider } from '@/hooks/useMiniPayProvider'
import { MiniPayError } from '@/components/MiniPayError'
import MiniPayIcon from '@/assets/minipay.svg'
import { track } from '@/utils/analytics'
import { replaceLegacyPaytrieWallet } from '@/utils/legacyPaytrieWallet'

const OTC_REQUIRED_ERROR_PATTERN =
  /^Transactions above (?<max>[\d.,]+) CAD must be submitted as an OTC order$/

type AddWalletProps = {
  wallet: string
  onSetWallet: (wallet: string) => void
  onSubmitSuccess: (tx: string, depositAddress: string | null) => void
  setLoading: (loading: boolean) => void
}

type ApiErrorContext = {
  apiAction?: string
  transactionType?: TransactionValue
  network?: NetworkValue
  stableCoin?: string
  errorDetails?: string
  quoteId?: number
  wallet?: string
  responseBody?: string
}

// Function to capture API errors in Sentry
const captureApiError = (error: Error, contextData: ApiErrorContext = {}) => {
  // Extract error patterns for better classification
  const errorMessage = error?.message || ''
  const isNetworkError =
    errorMessage.includes('network') || errorMessage.includes('connect')
  const isAuthError =
    errorMessage.includes('verify') ||
    errorMessage.includes('auth') ||
    errorMessage.includes('token')
  const isLimitError =
    errorMessage.includes('limit') || errorMessage.includes('exceed')
  const isInteracError =
    errorMessage.includes('interact') || errorMessage.includes('SF_003')

  // Create error classification for better grouping in Sentry
  const errorType = isNetworkError
    ? 'api.network_error'
    : isAuthError
      ? 'api.auth_error'
      : isLimitError
        ? 'api.limit_error'
        : isInteracError
          ? 'api.interac_error'
          : 'api.other_error'

  // Capture with enriched context
  Sentry.captureException(error, {
    tags: {
      error_type: errorType,
      transaction_type: contextData.transactionType || 'unknown',
      network: contextData.network || 'unknown',
      api_action: contextData.apiAction || 'unknown',
    },
    extra: {
      stableCoin: contextData.stableCoin,
      errorDetails: contextData.errorDetails,
      quoteId: contextData.quoteId,
      wallet: contextData.wallet,
      responseBody: contextData.responseBody,
    },
    level: 'error',
  })

  // Also log to console for local debugging
  console.error(`API Error (${errorType}):`, error, contextData)
}

export const AddWallet = ({
  wallet,
  onSetWallet,
  onSubmitSuccess,
  setLoading,
}: AddWalletProps) => {
  const { t } = useTranslation()
  const { enableCadcSolana } = useFeatureFlags()
  const [loading, setIsLoading] = useState(false)
  const state = useBuySellStore.getState()
  const email = usePageStateStore.use.email()
  const [error, setError] = useState('')
  const handleWalletConnectError = useCallback((nextError: Error) => {
    setError(nextError.message)
  }, [])
  const [showUseEthInfo, setShowUseEthInfo] = useState<boolean>(false)
  const [latestV2Quote, network] = [
    useBuySellStore.use.latestV2Quote(),
    useBuySellStore.use.network(),
  ]
  const navigate = useNavigate()
  const authToken = usePageStateStore.use.authToken() || ''
  const {
    data: currentUser,
    isLoading: isUserLoading,
    isError: isUserError,
  } = useCurrentUser(!!authToken)
  const userId = currentUser?.id ?? ''
  const referralCredit = currentUser?.referralCreditBalance ?? 0
  const [useReferral, setUseReferral] = useState(false)
  const [fiatAmount, stableCoinAmount] = [
    useBuySellStore.use.fiatAmount(),
    useBuySellStore.use.stableCoinAmount(),
  ]
  const [setReferralAdjustedFiatAmount, setReferralAdjustedCoinAmount] = [
    useBuySellStore.use.setReferralAdjustedFiatAmount(),
    useBuySellStore.use.setReferralAdjustedCoinAmount(),
  ]

  const [selectedWalletAccount] = useContext(SelectedWalletAccountContext)
  const walletInputStartedRef = useRef(false)
  const validationFailedTrackedRef = useRef(false)

  const isWalletLocked = Boolean(sessionStorage.getItem('walletAddress'))

  const {
    isAutoConnectEnabled,
    isMiniPay,
    isConnecting: isMiniPayConnecting,
    isConnected: isMiniPayConnected,
    address: miniPayAddress,
    error: miniPayError,
    retry: retryMiniPayConnect,
  } = useMiniPayProvider()

  const isMiniPayMode =
    isAutoConnectEnabled && (isMiniPay || Boolean(miniPayError))

  useEffect(() => {
    if (isAutoConnectEnabled && isMiniPayConnected && miniPayAddress) {
      onSetWallet(replaceLegacyPaytrieWallet(miniPayAddress))
    }
  }, [isAutoConnectEnabled, isMiniPayConnected, miniPayAddress, onSetWallet])

  useEffect(() => {
    if (network === Networks.sol && selectedWalletAccount) {
      onSetWallet(replaceLegacyPaytrieWallet(selectedWalletAccount.address))
    }
  }, [network, selectedWalletAccount, onSetWallet])

  useEffect(() => {
    if (!isUserError) return
    setError(t('addWallet.errors.couldNotFetch'))
    Sentry.addBreadcrumb({
      category: 'api.error',
      message: 'Failed to fetch user',
      level: 'error',
    })
    navigate('/EasyLogin')
  }, [isUserError, navigate, t])

  const applyReferralCredit = useCallback(
    async (checked: boolean) => {
      if (!checked) {
        setUseReferral(false)
        return
      }
      try {
        const isBuy = state.transactionType === TransactionType.BUY
        const quote = await getReferralQuote({
          transactionType: state.transactionType,
          stableCoin: state.stableCoin,
          network: state.network,
          payAmount: isBuy ? fiatAmount : stableCoinAmount,
        })
        if (isBuy) {
          setReferralAdjustedCoinAmount(quote.rightSideValue)
        } else {
          setReferralAdjustedFiatAmount(quote.rightSideValue)
        }
        setUseReferral(true)
      } catch (referralError) {
        setError(t('addWallet.errors.priceQuoteUnavailable'))
        captureApiError(referralError as Error, {
          apiAction: 'getReferralQuote',
          transactionType: state.transactionType,
          network,
        })
      }
    },
    [
      state.transactionType,
      state.stableCoin,
      state.network,
      fiatAmount,
      stableCoinAmount,
      setReferralAdjustedCoinAmount,
      setReferralAdjustedFiatAmount,
      network,
      t,
    ]
  )

  const validateAddress = () => isValidWalletAddressForNetwork(wallet, network)

  const submit = async () => {
    setIsLoading(true)
    setLoading(true)

    const normalizedWallet = replaceLegacyPaytrieWallet(wallet)
    if (normalizedWallet !== wallet) {
      onSetWallet(normalizedWallet)
    }

    Sentry.addBreadcrumb({
      message: `Starting Submit`,
      level: 'error',
      data: {
        type: state.transactionType,
        quoteId: latestV2Quote?.quoteId,
        stableCoin: state?.stableCoin,
        email,
        wallet: normalizedWallet,
        network: state.network,
        useReferral,
        token: authToken,
      },
    })

    const result =
      state.transactionType === TransactionType.SELL
        ? await submitSell({
            quoteId: latestV2Quote?.quoteId as number,
            stableCoin: state.stableCoin,
            amount: state.stableCoinAmount,
            wallet: normalizedWallet,
            network: state.network,
            useReferral,
          })
        : await submitBuy({
            quoteId: latestV2Quote?.quoteId ?? 0,
            wallet: normalizedWallet,
            amount: state.fiatAmount,
            stableCoin: state.stableCoin,
            network: state.network,
            useReferral,
          })
    const error = 'error' in result ? result.error : undefined
    const response = 'response' in result ? result.response : undefined

    if (error) {
      track('transaction_submit_failed', {
        tx_type: state.transactionType,
        coin: state.stableCoin,
        network: state.network,
        http_status: error.status,
        error_message: error.message.slice(0, 200),
      })
      const errorMessage = error.message
      const otcRequiredMax =
        OTC_REQUIRED_ERROR_PATTERN.exec(errorMessage)?.groups?.max
      if (otcRequiredMax) {
        setError(t('addWallet.errors.otcRequired', { max: otcRequiredMax }))
      } else if (
        errorMessage ===
        'Too many active transactions, please complete or cancel existing transactions before continuing'
      ) {
        setError(t('addWallet.errors.tooManyActive'))
      } else if (
        errorMessage ===
        'Too many cancelled/expired transactions today, please try again tomorrow'
      ) {
        setError(t('addWallet.errors.tooManyCancelled'))
      } else if (
        errorMessage.startsWith('You have exceeded your daily limit by')
      ) {
        setError(t('addWallet.errors.dailyLimitExceeded'))
      } else if (
        errorMessage ===
        'Unable to process transaction. You have not been verified by PayTrie. Please allow up to 24 hours for verification'
      ) {
        setError(t('addWallet.errors.notVerified'))
      } else if (
        errorMessage ===
        'Unable to process transaction. You have not verified your email address. Please check your inbox and spam folder for our verification email.'
      ) {
        setError(t('addWallet.errors.emailNotVerified'))
      } else if (
        errorMessage ===
        'Unable to process transaction. You have not verified your phone number.'
      ) {
        setError(t('addWallet.errors.phoneNotVerified'))
      } else if (errorMessage === 'ERROR CONNECTING WITH INTERACT') {
        setError(t('addWallet.errors.interacError'))
      } else if (
        errorMessage ===
        'Unable to send rmt.  Error code SF_003.  Please try again later or contact us at support@paytrie.com'
      ) {
        setError(t('addWallet.errors.interacError'))
      } else if (
        errorMessage ===
        'Unable to process request at the moment.  Please try again later or contact us at support@paytrie.com'
      ) {
        setError(t('addWallet.errors.interacError'))
      } else if (errorMessage) {
        setError(t('addWallet.errors.unableToProcess'))
      } else {
        setError(t('addWallet.errors.unknownError'))
        captureApiError(new Error(`HTTP ${error.status}`), {
          apiAction:
            state.transactionType === TransactionType.SELL
              ? 'submitSell'
              : 'submitBuy',
          transactionType: state.transactionType,
          network: state.network,
          stableCoin: state.stableCoin,
          quoteId: latestV2Quote?.quoteId,
          wallet,
          responseBody: error.responseBody,
        })
      }
      setIsLoading(false)
    } else if (response) {
      if (userId) {
        saveUserTokenPreference(
          userId,
          state.stableCoin,
          state.network,
          enableCadcSolana
        )
      }

      track('transaction_submitted', {
        tx_id: response.data.tx,
        tx_type: state.transactionType,
        coin: state.stableCoin,
        network: state.network,
        fiat_amount: fiatAmount,
        stable_coin_amount: stableCoinAmount,
      })

      setIsLoading(false)
      onSubmitSuccess(response.data.tx, response.data.depositAddress)
    }
  }

  if (loading || isUserLoading) {
    return (
      <div
        data-testid="container-add-wallet-loading"
        className="my-auto flex flex-col justify-center items-center"
      >
        <LoadingIcon />
        <EllipsisAnimation>{t('addWallet.processing')}</EllipsisAnimation>
      </div>
    )
  }

  if (isAutoConnectEnabled && miniPayError) {
    return (
      <div data-testid="container-add-wallet" className="flex flex-col gap-5">
        <BackButton
          data-testid="btn-add-wallet-back"
          onClick={() => navigate('/')}
          className="absolute top-12"
        >
          &#x27f5;
        </BackButton>
        <MiniPayError
          error={miniPayError}
          onRetry={retryMiniPayConnect}
          showRetry={true}
        />
      </div>
    )
  }

  if (isAutoConnectEnabled && isMiniPayConnecting) {
    return (
      <div
        data-testid="container-add-wallet-minipay-connecting"
        className="my-auto flex flex-col justify-center items-center"
      >
        <LoadingIcon />
        <EllipsisAnimation>
          {t('addWallet.connectingWallet', {
            defaultValue: 'Connecting wallet',
          })}
        </EllipsisAnimation>
      </div>
    )
  }

  return (
    <div data-testid="container-add-wallet" className="flex flex-col gap-4">
      <BackButton
        data-testid="btn-add-wallet-back"
        onClick={() => navigate('/')}
        className="absolute top-12"
      >
        &#x27f5;
      </BackButton>
      <h1
        data-testid="text-add-wallet-title"
        className="text-lg tracking-tight uppercase"
      >
        {t('addWallet.title', {
          network: NetworkOptions[network as NetworkValue].name,
        })}
      </h1>

      {isMiniPayMode && (
        <div
          data-testid="minipay-indicator"
          className="flex items-center gap-2 px-3 py-2 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-none"
        >
          <img src={MiniPayIcon} alt="MiniPay" className="w-5 h-5" />
          <span className="text-sm text-emerald-700 dark:text-emerald-300">
            {t('addWallet.miniPayMode')}
          </span>
        </div>
      )}

      <div
        data-testid="container-add-wallet-input"
        className="flex gap-2 items-start"
      >
        <div className="flex-1">
          <TextInput
            data-testid="input-add-wallet-address"
            placeholder={t('addWallet.placeholder')}
            value={wallet}
            maxLength={50}
            onChange={(e) => {
              const next = e.target.value
              if (!walletInputStartedRef.current && next.length > 0) {
                walletInputStartedRef.current = true
                track('wallet_input_started', { network })
              }
              onSetWallet(replaceLegacyPaytrieWallet(next))
            }}
            onBlur={() => {
              if (
                wallet.length >= 10 &&
                !validateAddress() &&
                !validationFailedTrackedRef.current
              ) {
                validationFailedTrackedRef.current = true
                track('wallet_validation_failed', {
                  network,
                  wallet_length: wallet.length,
                })
              }
            }}
            disabled={isWalletLocked || isMiniPayMode}
            className={
              isWalletLocked || isMiniPayMode
                ? 'bg-gray-100 dark:bg-gray-800 cursor-not-allowed'
                : ''
            }
          />
        </div>
        {!isWalletLocked && !isMiniPayMode && (
          <SimpleWalletConnect
            data-testid="btn-add-wallet-connect"
            network={network}
            onWalletConnect={(address) =>
              onSetWallet(replaceLegacyPaytrieWallet(address))
            }
            onError={handleWalletConnectError}
            isValidManualAddress={validateAddress()}
            currentWallet={wallet}
          />
        )}
      </div>

      <BuyOrderSummary
        data-testid="container-add-wallet-summary"
        useReferral={useReferral}
      />

      {referralCredit > 0 && (
        <div data-testid="container-add-wallet-referral">
          <ReferralAccountText>
            <Checkbox
              data-testid="check-add-wallet-referral"
              label={t('addWallet.useReferralCredit')}
              checked={useReferral}
              onCheckedChange={applyReferralCredit}
            />
          </ReferralAccountText>
        </div>
      )}

      <div data-testid="text-add-wallet-error" className="my-2 text-rose-600">
        {error}
      </div>

      <Button
        data-testid="btn-add-wallet-submit"
        disabled={loading || !validateAddress()}
        onClick={submit}
        className="inline-flex items-center py-8 px-4 w-full justify-center lg:gap-2"
      >
        {!validateAddress()
          ? t('addWallet.invalidAddress')
          : t('addWallet.submit')}
        <span className="ml-5 mb-2">&#x27f6;</span>
      </Button>

      {showUseEthInfo && (
        <UseEthInfo
          data-testid="container-add-wallet-eth-info"
          onHide={() => {
            setShowUseEthInfo(false)
          }}
        />
      )}
    </div>
  )
}

type SummaryProps = {
  useReferral: boolean
}

const BuyOrderSummary = ({ useReferral }: SummaryProps) => {
  const { t } = useTranslation()
  return (
    <div>
      <h2 className="text-lg font-normal tracking-tight mb-2 mt-2">
        {t('addWallet.orderSummary')}
      </h2>
      <SummaryRow useReferral={useReferral} />
    </div>
  )
}
